import type { TableOrder, TablePayment, VendorShow } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import {
  cancelTableOrder,
  claimTable,
  loadVendorShows,
  noteTablePaid,
  openTablePayment,
  redeemReservation,
} from "../../../../core/ops/vendor-show-ops";
import { failedForm, failedLoad, idleForm, loaded, loading, savedForm } from "../../../../core/primitives/load-status";
import { can } from "../../../../core/primitives/store-role";
import {
  type OpenTablePayment,
  VendorShowsSectionActions,
  VendorShowsSectionState,
} from "./vendor-shows-section-model";

export type VendorShowsSectionDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "stripePayments"
>;

/** How often, and how long, a paid table is read again until the server has Stripe's word. */
const CONFIRM_EVERY_MS = 1500;
const CONFIRM_READS = 10;
/** setTimeout's ceiling; a later deadline would fire at once. */
const MAX_TIMER_MS = 2_147_483_647;

export const TABLE_YOURS = (tableLabel: string) =>
  `Table ${tableLabel} is yours. From the day before the show, you can switch to your booth there.`;
export const PAYMENT_PENDING =
  "Payment received. The table is yours as soon as Stripe confirms it, usually within seconds; read the shows again if it still waits.";
export const HOLD_RAN_OUT =
  "The hold ran out before the payment was made. The table goes back on sale in a couple of minutes; take one again then.";
export const SHOW_GONE = "That show is no longer listed, so its table is no longer held for you.";
export const SHOWS_TO_SELL_AT_FAILED_TO_LOAD = "The shows could not be read. Read them again.";
export const TAKE_FAILED_ON_THE_WAY =
  "Something went wrong on the way. Read the shows again: the table may be held for you.";
export const PAY_FAILED_ON_THE_WAY = "Something went wrong on the way. Try Pay again.";
export const REDEEMED = "The reserved table is your shop's now, at its booth for the show.";
export const ENTER_CODE = "Enter the reservation code from the email, like K7QX-M2PD.";
export const REDEEM_FAILED_ON_THE_WAY =
  "Something went wrong on the way. Read the shows again: the table may be yours.";
export const GIVE_UP_FAILED_ON_THE_WAY =
  "Something went wrong on the way. Read the shows again: the table may be back on sale.";

/** Where a paid order stands in the shows as read: its money gone back, the shop's table, or not yet known. */
function settledAs(shows: readonly VendorShow[], payment: OpenTablePayment): "waiting" | "yours" | TableOrder {
  const show = shows.find((s) => s.id === payment.eventId);
  if (!show) return "waiting";
  // A sold table stays on record when its show is cancelled, so the refund is looked for first.
  const refund = show.myRefunds.find((r) => r.id === payment.order.id);
  if (refund) return refund;
  return show.myTables.some((t) => t.number === payment.order.tableNumber) ? "yours" : "waiting";
}

/** What became of the shop's money, in its refund's words: sent back, or still owed. */
function wentBack(refund: TableOrder): string {
  const reason = refund.refundReason ?? "the table could not be had.";
  return refund.status === "FAILED" ? `The payment is owed back to you: ${reason}` : `The payment went back: ${reason}`;
}

/**
 * Other shops' published shows, each with its kinds of table and what is left.
 * A Manager or an Owner takes one table per show: held for five minutes while
 * they pay for it here, with Stripe's form for the host's account, and theirs
 * once Stripe tells the server. A refusal reads the list again, since it may
 * say the counts on screen are out of date; a call that fails on its way frees
 * the section, says so and lets the error through.
 */
export class VendorShowsSectionController extends BaseController implements VendorShowsSectionActions {
  readonly state = new VendorShowsSectionState();
  readonly now: () => Date;
  readonly wait: (ms: number) => Promise<void>;
  #storeId: string | null = null;
  #lapse: ReturnType<typeof setTimeout> | null = null;

  constructor(
    subtree: SubtreeModel,
    readonly deps: VendorShowsSectionDeps,
    {
      now = () => new Date(),
      wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    }: { now?: () => Date; wait?: (ms: number) => Promise<void> } = {},
  ) {
    super();
    subtree.put(VendorShowsSectionState, this.state);
    subtree.put(VendorShowsSectionActions, this);
    this.now = now;
    this.wait = wait;
    this.autoDispose(() => this.#clearLapse());
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.mayTake.value = store ? can(store.myRole, "takeTables") : false;
      const storeId = store?.id ?? null;
      if (storeId === this.#storeId) return;
      if (this.#storeId) this.state.action.value = idleForm;
      this.#storeId = storeId;
      this.#close();
      this.state.busyId.value = null;
      this.state.confirmed.value = new Set();
      this.state.shows.value = [];
      this.state.reservations.value = [];
      if (storeId) void this.#load(storeId);
    }, [deps.storeRepository]);
  }

  reload = async () => {
    if (this.#storeId) await this.#load(this.#storeId);
  };

  take = async (eventId: string, kindId: string) => {
    const storeId = this.#storeId;
    if (!storeId || this.state.busyId.value || this.state.paymentPhase.value !== "open") return;
    this.state.busyId.value = kindId;
    this.state.action.value = idleForm;
    let r: Awaited<ReturnType<typeof claimTable>>;
    try {
      r = await claimTable({ ...this.deps, storeId, eventId, kindId });
    } catch (err) {
      if (this.#here(storeId)) {
        this.state.busyId.value = null;
        this.state.action.value = failedForm(TAKE_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (!this.#here(storeId)) return;
    this.state.busyId.value = null;
    void this.#load(storeId);
    if (!r.ok) {
      this.state.action.value = failedForm(r.errors);
      return;
    }
    const { order, payment } = r.value;
    if (payment) {
      this.#open(eventId, order, payment);
      return;
    }
    noteTablePaid(this.deps);
    this.state.action.value = savedForm(TABLE_YOURS(order.tableLabel));
  };

  pay = async (eventId: string) => {
    const storeId = this.#storeId;
    const order = this.#orderAt(eventId);
    if (!storeId || !order || this.state.busyId.value || this.state.payment.value) return;
    this.state.busyId.value = order.id;
    this.state.action.value = idleForm;
    let r: Awaited<ReturnType<typeof openTablePayment>>;
    try {
      r = await openTablePayment({ ...this.deps, storeId, orderId: order.id });
    } catch (err) {
      if (this.#here(storeId)) {
        this.state.busyId.value = null;
        this.state.action.value = failedForm(PAY_FAILED_ON_THE_WAY);
      }
      throw err;
    }
    if (!this.#here(storeId)) return;
    this.state.busyId.value = null;
    if (!r.ok) {
      this.state.action.value = failedForm(r.errors);
      void this.#load(storeId);
      return;
    }
    this.#open(eventId, order, r.value);
  };

  paymentStarted = () => {
    if (!this.state.payment.value || this.state.paymentPhase.value !== "open" || this.state.busyId.value) return false;
    this.state.paymentPhase.value = "paying";
    this.state.action.value = idleForm;
    return true;
  };

  paymentFailed = (message: string) => {
    const payment = this.state.payment.value;
    if (!payment || this.state.paymentPhase.value !== "paying") return;
    this.state.paymentPhase.value = "open";
    this.state.action.value = failedForm(message);
    // The hold may have run out while Stripe was asking.
    if (payment.order.expiresAt.getTime() <= this.now().getTime()) void this.#lapsed();
  };

  paymentConfirmed = async () => {
    const storeId = this.#storeId;
    const payment = this.state.payment.value;
    if (!storeId || !payment || this.state.paymentPhase.value !== "paying") return;
    this.#clearLapse();
    this.state.paymentPhase.value = "confirming";
    this.state.action.value = idleForm;
    // Stripe has the money: the order is not offered for paying or giving up again.
    this.state.confirmed.value = new Set([...this.state.confirmed.value, payment.order.id]);
    for (let read = 1; ; read++) {
      try {
        await this.#load(storeId);
      } catch {
        // A read that fails on its way is one of the ten; the last sentence says to read again.
      }
      if (!this.#here(storeId) || this.state.payment.value !== payment) return;
      if (this.#settled(payment)) return;
      if (read === CONFIRM_READS) break;
      await this.wait(CONFIRM_EVERY_MS);
      if (!this.#here(storeId) || this.state.payment.value !== payment) return;
    }
    this.#close();
    this.state.action.value = savedForm(PAYMENT_PENDING);
  };

  cancel = async (eventId: string) => {
    const storeId = this.#storeId;
    const panel = this.state.payment.value?.eventId === eventId ? this.state.payment.value : null;
    // The panel's own order, even once a read no longer lists it.
    const order = panel ? panel.order : this.#orderAt(eventId);
    // Nothing is given up while a card is with Stripe, at this show or another.
    if (!storeId || !order || this.state.busyId.value || this.state.paymentPhase.value !== "open") return;
    this.state.busyId.value = eventId;
    this.state.action.value = idleForm;
    let r: Awaited<ReturnType<typeof cancelTableOrder>>;
    try {
      r = await cancelTableOrder({ ...this.deps, storeId, orderId: order.id });
    } catch (err) {
      if (this.#here(storeId)) {
        this.state.busyId.value = null;
        this.#closeFor(order.id);
        this.state.action.value = failedForm(GIVE_UP_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (!this.#here(storeId)) return;
    this.state.busyId.value = null;
    // Given up or refused, the order's panel goes; the shows read again say what is left of it.
    this.#closeFor(order.id);
    this.state.action.value = r.ok ? savedForm("The table is back on sale.") : failedForm(r.errors);
    void this.#load(storeId);
  };

  setCode = (code: string) => {
    this.state.code.value = code;
  };

  redeem = async (code?: string) => {
    const storeId = this.#storeId;
    if (!storeId || this.state.busyId.value || this.state.payment.value) return;
    const typed = code === undefined;
    const value = (code ?? this.state.code.value).trim();
    if (!value) {
      this.state.action.value = failedForm(ENTER_CODE);
      return;
    }
    this.state.busyId.value = `redeem:${value}`;
    this.state.action.value = idleForm;
    let r: Awaited<ReturnType<typeof redeemReservation>>;
    try {
      r = await redeemReservation({ ...this.deps, storeId, code: value });
    } catch (err) {
      if (this.#here(storeId)) {
        this.state.busyId.value = null;
        this.state.action.value = failedForm(REDEEM_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (!this.#here(storeId)) return;
    this.state.busyId.value = null;
    if (!r.ok) {
      this.state.action.value = failedForm(r.errors);
      return;
    }
    if (typed) this.state.code.value = "";
    noteTablePaid(this.deps);
    this.state.action.value = savedForm(REDEEMED);
    void this.#load(storeId);
  };

  /** The order being paid for at the show, unless Stripe already has the money for it. */
  #orderAt(eventId: string): TableOrder | null {
    const order = this.state.shows.value.find((s) => s.id === eventId)?.myOrder ?? null;
    return order && !this.state.confirmed.value.has(order.id) ? order : null;
  }

  #here(storeId: string): boolean {
    return !this.isDisposed && storeId === this.#storeId;
  }

  #open(eventId: string, order: TableOrder, payment: TablePayment): void {
    this.#clearLapse();
    this.state.paymentPhase.value = "open";
    this.state.payment.value = {
      eventId,
      order,
      clientSecret: payment.clientSecret,
      // A script that fails to load reads as none: the form says payments cannot be taken here.
      stripe: this.deps.stripePayments.forAccount(payment.accountId).catch(() => null),
    };
    const left = order.expiresAt.getTime() - this.now().getTime();
    this.#lapse = setTimeout(
      () => {
        this.#lapse = null;
        // A card being confirmed is left to finish; `paymentFailed` looks at the clock again.
        if (this.state.paymentPhase.value === "open") void this.#lapsed();
      },
      Math.min(Math.max(0, left), MAX_TIMER_MS),
    );
  }

  /** Closes the payment panel if it still shows the order. */
  #closeFor(orderId: string): void {
    if (this.state.payment.value?.order.id === orderId) this.#close();
  }

  /** Closes the payment and says what became of it, once the shows as read tell; false while they do not. */
  #settled(payment: OpenTablePayment): boolean {
    const settled = settledAs(this.state.shows.value, payment);
    if (settled === "waiting") return false;
    this.#closeFor(payment.order.id);
    if (settled === "yours") {
      noteTablePaid(this.deps);
      this.state.action.value = savedForm(TABLE_YOURS(payment.order.tableLabel));
    } else {
      this.state.action.value = failedForm(wentBack(settled));
    }
    return true;
  }

  /** The hold ran out: the payment closes, and the shows read again say if it was made elsewhere after all. */
  async #lapsed(): Promise<void> {
    const storeId = this.#storeId;
    const payment = this.state.payment.value;
    this.#close();
    const said = failedForm(HOLD_RAN_OUT);
    this.state.action.value = said;
    if (!storeId || !payment) return;
    await this.#load(storeId);
    if (this.#here(storeId) && this.state.action.value === said) this.#settled(payment);
  }

  #close(): void {
    this.#clearLapse();
    this.state.payment.value = null;
    this.state.paymentPhase.value = "open";
  }

  #clearLapse(): void {
    if (this.#lapse) clearTimeout(this.#lapse);
    this.#lapse = null;
  }

  async #load(storeId: string): Promise<void> {
    this.state.status.value = loading;
    let r: Awaited<ReturnType<typeof loadVendorShows>>;
    try {
      r = await loadVendorShows({ ...this.deps, storeId });
    } catch (err) {
      if (this.#here(storeId)) this.state.status.value = { kind: "error", message: SHOWS_TO_SELL_AT_FAILED_TO_LOAD };
      throw err;
    }
    if (!this.#here(storeId)) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.shows.value = r.value.shows;
    this.state.reservations.value = r.value.reservations;
    this.state.status.value = loaded;
    // A payment whose show has left the list has no row to be shown in, and no table held for it any more.
    const payment = this.state.payment.value;
    if (payment && this.state.paymentPhase.value === "open" && !r.value.shows.some((s) => s.id === payment.eventId)) {
      this.#close();
      this.state.action.value = failedForm(SHOW_GONE);
    }
  }
}
